import React, { useCallback, useEffect, useState } from 'react';
import { CartProvider, useCart } from './context/CartContext';
import { VendorProvider } from './context/VendorContext';
import VendorGate from './components/VendorGate';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Catalogo from './pages/Catalogo';
import Pedidos from './pages/Pedidos';
import HistorialAtenciones from './pages/HistorialAtenciones';
import PedidoDetalle from './pages/PedidoDetalle';
import MisClientes from './pages/MisClientes';
import Productos from './pages/Productos';
import CartPanel from './components/CartPanel';
import BrandLogo from './components/BrandLogo';
import SilentVisitaTracker from './components/SilentVisitaTracker';
import OfflinePedidoBanner from './components/OfflinePedidoBanner';
import { useVendor } from './context/VendorContext';
import { api, getAuthToken } from './services/api';
import { clearSucursalesCache } from './services/realtime';
import { usePedidoStatusAlert } from './hooks/usePedidoStatusAlert';
import useOfflinePedidoSync from './hooks/useOfflinePedidoSync';
import useRealtimeSync from './hooks/useRealtimeSync';
import useCartReserva from './hooks/useCartReserva';
import { unlockPedidoStatusSound } from './utils/pedidoStatusSound';
import { enrichItemsWithImages } from './utils/productImageLookup';

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div
      className={`toast toast-${type}`}
      role="status"
      aria-live="polite"
      onClick={onClose}
    >
      {message}
    </div>
  );
}

function AppShell() {
  const [page, setPage] = useState('dashboard');
  const [params, setParams] = useState({});
  const [refreshPedidos, setRefreshPedidos] = useState(0);
  const [mode, setMode] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'ok' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('oh_sidebar_collapsed') !== '0';
    } catch {
      return true;
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCollapsed, setCartCollapsed] = useState(() => {
    try {
      return localStorage.getItem('oh_cart_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [wideLayout, setWideLayout] = useState(() => {
    try {
      return window.matchMedia('(min-width: 900px)').matches;
    } catch {
      return false;
    }
  });
  const { totalItems, state: cartState, dispatch: cartDispatch } = useCart();
  useCartReserva(cartState.items, {
    canal: 'app',
    onAdjusted: (adjusted) => {
      const codes = adjusted.map((a) => a.codigo).join(', ');
      setToast({
        message: `Algunos ítems se ajustaron por stock (reserva 30 min): ${codes}`,
        type: 'error',
      });
    },
  });
  const { vendedor } = useVendor();
  const [refreshClientes, setRefreshClientes] = useState(0);
  const [guardadosOpen, setGuardadosOpen] = useState(false);
  const [guardadoFocusId, setGuardadoFocusId] = useState(null);

  const getToken = useCallback(() => getAuthToken(), []);

  const onRealtimeInvalidate = useCallback((domains) => {
    // El catálogo se refresca solo (usePaginatedProductos escucha oh:invalidate)
    // No borrar el caché del catálogo: un cambio en admin no debe
    // vaciar la grilla ni mandar al vendedor al primer producto.
    if (domains.includes('client')) {
      clearSucursalesCache();
      setRefreshClientes((n) => n + 1);
    }
    if (domains.includes('order')) {
      setRefreshPedidos((n) => n + 1);
    }
  }, []);

  const onRealtimeReconnect = useCallback(() => {
    // Solo refetch liviano de listas; no tumbar catálogo cacheado (tormenta con 7 tablets).
    setRefreshClientes((n) => n + 1);
    setRefreshPedidos((n) => n + 1);
  }, []);

  useRealtimeSync({
    app: 'vendedor',
    enabled: !!vendedor,
    getToken,
    onInvalidate: onRealtimeInvalidate,
    onReconnect: onRealtimeReconnect,
  });

  // Ajustar precios del carrito cuando llega un catálogo fresco
  useEffect(() => {
    if (!cartState.items.length) return undefined;
    const onInv = async (ev) => {
      const detail = ev.detail || {};
      const domains = detail.domains || [];
      if (!domains.includes('product') && !domains.includes('inventory')) return;
      if (detail.reason === 'producto_eliminado' && detail.codigo) {
        cartDispatch({ type: 'REMOVE_BY_CODIGO', codigo: detail.codigo });
        return;
      }
      try {
        const codes = [...new Set(cartState.items.map((i) => i.codigo))];
        const byCodigo = {};
        await Promise.all(
          codes.map(async (codigo) => {
            try {
              const res = await api.getProducto(codigo);
              if (res.data) byCodigo[String(codigo)] = res.data;
            } catch (err) {
              // 404 = pausado / oculto para la app; sin status = fallo de red (no tocar el carrito)
              if (err?.status === 404) byCodigo[String(codigo)] = { __unavailable: true };
            }
          })
        );
        cartDispatch({ type: 'RECONCILE_PRODUCTS', byCodigo });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('oh:invalidate', onInv);
    return () => window.removeEventListener('oh:invalidate', onInv);
  }, [cartState.items, cartDispatch]);

  useEffect(() => {
    const notice = cartState._reconcile;
    if (!notice) return;
    const parts = [];
    if (notice.priceChanges?.length) {
      parts.push(
        notice.priceChanges.length === 1
          ? `Precio actualizado: ${notice.priceChanges[0].codigo}`
          : `${notice.priceChanges.length} precios del carrito se actualizaron`
      );
    }
    if (notice.removed?.length) {
      parts.push(
        notice.removed.length === 1
          ? `Se quitó ${notice.removed[0]} (ya no disponible)`
          : `${notice.removed.length} productos no disponibles se quitaron del carrito`
      );
    }
    if (parts.length) {
      setToast({ message: parts.join(' · '), type: 'ok' });
    }
    cartDispatch({ type: 'CLEAR_RECONCILE_NOTICE' });
  }, [cartState._reconcile, cartDispatch]);

  useEffect(() => {
    api.ping().then((r) => setMode(r.data?.mode || '')).catch(() => setMode('offline'));
    const onOnline = () => {
      api.ping().then((r) => setMode(r.data?.mode || '')).catch(() => setMode('offline'));
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia('(min-width: 900px)');
    } catch {
      return undefined;
    }
    const onChange = () => setWideLayout(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (!toast.message) return undefined;
    const ms = toast.message.length > 48 ? 4200 : 2400;
    const t = setTimeout(() => setToast({ message: '', type: 'ok' }), ms);
    return () => clearTimeout(t);
  }, [toast.message]);

  useEffect(() => {
    if (!cartOpen) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') setCartOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cartOpen]);

  const showToast = useCallback((message, type = 'ok') => {
    setToast({ message, type });
  }, []);

  const onPedidoNotify = useCallback(
    (_info) => {
      // Sin avisos de estado al vendedor (ni sonido ni toast)
      setRefreshPedidos((n) => n + 1);
    },
    []
  );

  // enabled: false → no suena ni anuncia cambios de estado
  const { updatesCount, clearUpdates } = usePedidoStatusAlert({
    onNotify: onPedidoNotify,
    enabled: false,
  });

  const onOfflineSynced = useCallback(
    (sent) => {
      const n = sent.length;
      if (n === 1) {
        showToast(`Pedido ${sent[0].pedido?.numero || ''} enviado (estaba guardado)`, 'ok');
      } else {
        showToast(`${n} pedidos enviados a oficina (estaban en tablet)`, 'ok');
      }
      setRefreshPedidos((x) => x + 1);
    },
    [showToast]
  );

  const offlineSync = useOfflinePedidoSync({
    enabled: !!vendedor,
    vendedorId: vendedor?.id || null,
    onSynced: onOfflineSynced,
  });

  useEffect(() => {
    // Esperar gesto del usuario antes de tocar AudioContext
    unlockPedidoStatusSound();
  }, [vendedor?.id]);

  function toggleSidebarCollapse() {
    setSidebarCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem('oh_sidebar_collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  function navigate(target, p = {}) {
    setPage(target);
    setParams(p);
    setSidebarOpen(false);
  }

  function onPedidoCreado(pedido, opts = {}) {
    const { navigate: goDetalle = true, toast = true } = opts;
    if (toast) {
      showToast(`Pedido ${pedido.numero} enviado a oficina`);
    }
    offlineSync.refresh();
    setRefreshPedidos((n) => n + 1);
    if (goDetalle) {
      setCartOpen(false);
      if (pedido?.id) navigate('pedido-detalle', { id: pedido.id });
    }
  }

  function onPedidoGuardadoOffline(entry, msg, opts = {}) {
    const { navigate: leaveCart = true, toast = true } = opts;
    if (toast && msg !== null) {
      showToast(msg || 'Pedido guardado en la tablet — se enviará al volver internet', 'ok');
    }
    offlineSync.refresh();
    setRefreshPedidos((n) => n + 1);
    if (leaveCart) setCartOpen(false);
  }

  function onAgregarProductosGuardado(entry) {
    if (!entry?.payload) return;
    const p = entry.payload;
    const enriched = enrichItemsWithImages(p.items || []);
    cartDispatch({
      type: 'HYDRATE_FROM_SAVED',
      payload: { ...p, items: enriched },
      items: enriched,
      pedidoMeta: {
        transportadora: p.transportadora || '',
        transportadora_otra: p.transportadora_otra || '',
        horario_recibo: p.horario_recibo || '',
        tipo_documento: p.tipo_documento || 'remision',
        forma_pago: p.forma_pago || 'contado',
      },
      cliente: {
        cliente_id: p.cliente_id || null,
        sucursal_id: p.sucursal_id || null,
        sucursal_codigo: p.sucursal_codigo || '',
        nombre: p.cliente_nombre || '',
        telefono: p.cliente_telefono || '',
        direccion: p.sucursal_direccion || '',
        notas: p.notas || '',
        vendedor: p.vendedor || '',
        vendedor_id: p.vendedor_id || null,
      },
    });
    try {
      sessionStorage.setItem('oh_editing_offline_pedido_id', String(entry.id));
    } catch {
      /* ignore */
    }
    setGuardadosOpen(false);
    navigate('pedido');
    setCartOpen(true);
    showToast('Puedes agregar más productos y volver a guardar o enviar a oficina');
  }

  const cartPanelProps = {
    onPedidoCreado,
    onPedidoGuardadoOffline,
    collapsed: wideLayout ? cartCollapsed : false,
    onToggleCollapse: wideLayout
      ? () => {
          setCartCollapsed((prev) => {
            const next = !prev;
            try {
              localStorage.setItem('oh_cart_collapsed', next ? '1' : '0');
            } catch {
              /* ignore */
            }
            return next;
          });
        }
      : undefined,
  };

  const showCart = page === 'pedido';

  function renderPage() {
    switch (page) {
      case 'dashboard':
        return (
          <Dashboard
            onNavigate={navigate}
            onOpenGuardados={() => setGuardadosOpen(true)}
          />
        );
      case 'pedido':
        return <Catalogo onToast={showToast} />;
      case 'mis-clientes':
        return (
          <MisClientes
            onToast={showToast}
            refreshKey={refreshClientes}
            onSelectForPedido={(s) => {
              showToast(`${s.nombre} — listo para el pedido`);
              navigate('pedido');
            }}
          />
        );
      case 'historial':
        return <HistorialAtenciones onToast={showToast} refreshKey={refreshClientes} />;
      case 'pedidos':
        return (
          <Pedidos
            refreshKey={refreshPedidos}
            onSelect={(id) => navigate('pedido-detalle', { id })}
            onOpenLocal={(entry) => {
              if (!entry?.id) return;
              setGuardadoFocusId(entry.id);
              setGuardadosOpen(true);
            }}
          />
        );
      case 'pedido-detalle':
        return (
          <PedidoDetalle
            id={params.id}
            onBack={() => navigate('pedidos')}
            onUpdated={() => setRefreshPedidos((n) => n + 1)}
          />
        );
      case 'clientes':
        return (
          <MisClientes
            onToast={showToast}
            refreshKey={refreshClientes}
            onSelectForPedido={(s) => {
              showToast(`${s.nombre} — listo para el pedido`);
              navigate('pedido');
            }}
          />
        );
      case 'productos':
        return <Productos onToast={showToast} />;
      default:
        return <Dashboard onNavigate={navigate} />;
    }
  }

  return (
    <div className="app app-layout">
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar
        className={`${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`.trim()}
        page={page}
        onNavigate={(target, p) => {
          if (target === 'pedidos') clearUpdates();
          navigate(target, p);
        }}
        mode={mode.toUpperCase()}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        pedidosBadge={updatesCount}
      />

      <div className="app-content">
        <header className={`topbar ${showCart ? 'topbar-catalog' : ''}`.trim()}>
          <button type="button" className="menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Menú">
            ☰
          </button>
          <button
            type="button"
            className="menu-btn desktop-only"
            onClick={toggleSidebarCollapse}
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Minimizar menú'}
          >
            {sidebarCollapsed ? '»' : '«'}
          </button>
          <BrandLogo size="sm" light />
          {vendedor && !showCart ? <span className="topbar-vendor">{vendedor.nombre}</span> : null}
          {showCart ? (
            <button type="button" className="cart-toggle" onClick={() => setCartOpen(true)}>
              🛒
              {totalItems > 0 ? <span className="cart-toggle-badge">{totalItems}</span> : null}
            </button>
          ) : (
            <span className="topbar-spacer" />
          )}
        </header>

        <OfflinePedidoBanner
          pending={offlineSync.pending}
          syncing={offlineSync.syncing}
          online={offlineSync.online}
          queue={offlineSync.queue}
          history={offlineSync.history}
          onSync={() => offlineSync.syncNow()}
          onClearHistory={offlineSync.clearHistory}
          onRemove={offlineSync.removePedido}
          open={guardadosOpen}
          onOpenChange={setGuardadosOpen}
          onAgregarProductos={onAgregarProductosGuardado}
          focusEntryId={guardadoFocusId}
          onFocusEntryConsumed={() => setGuardadoFocusId(null)}
        />

        <div className={`app-body ${showCart ? 'with-cart' : ''}`}>
          <main className="app-main">{renderPage()}</main>

          {showCart && wideLayout ? (
            <div className={`cart-desktop${cartCollapsed ? ' cart-desktop--collapsed' : ''}`}>
              <CartPanel {...cartPanelProps} />
            </div>
          ) : null}

          {showCart && !wideLayout ? (
            <>
              <div className={`cart-overlay ${cartOpen ? 'open' : ''}`} onClick={() => setCartOpen(false)} />
              <div
                className={`cart-drawer ${cartOpen ? 'open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Pedido actual"
              >
                <button type="button" className="cart-drawer-close" onClick={() => setCartOpen(false)} aria-label="Cerrar pedido">
                  ✕
                </button>
                <CartPanel {...cartPanelProps} />
              </div>
              {!cartOpen ? (
                <button
                  type="button"
                  className="cart-fab"
                  onClick={() => setCartOpen(true)}
                  aria-label="Abrir pedido"
                >
                  🛒
                  {totalItems > 0 ? <span className="cart-fab-badge">{totalItems}</span> : null}
                  {totalItems > 0 ? <span className="cart-fab-label">Ver pedido</span> : null}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'ok' })} />
    </div>
  );
}

export default function App() {
  return (
    <VendorProvider>
      <VendorGate>
        <CartProvider>
          <SilentVisitaTracker />
          <AppShell />
        </CartProvider>
      </VendorGate>
    </VendorProvider>
  );
}
